import { RouterModule, Routes } from '@angular/router';
import { HomePageComponent } from "./common/pages/home/home";
import { Error401PageComponent } from "./common/pages/error-401/error-401";
import { Error404PageComponent } from "./common/pages/error-404/error-404";
import { IsAppReadyGuard } from "./common/guards/is-app-ready";
import { HasHomepageRedirectGuard } from "./common/guards/has-homepage-redirect";
import { LogoutPageComponent } from "./common/pages/logout/logout";
import { HasPendingChangesGuard } from "./common/guards/has-pending-changes";
import { IsLoggedInGuard } from "./common/guards/is-logged-in";
import {
  OrganizationsOrganizationSelectPageComponent
} from "./organizations/pages/organization-select/organization-select";
import { OrganizationsHomePageComponent } from "./organizations/pages/home/home";
import { IsOrganizationMemberGuard } from "./organizations/guards/is-organization-member";
import { AthleteViewGuardService } from "./common/guards/athlete-view-guard.service";
import { OrganizationResolver } from "./organizations/resolvers/organization-resolver.service";
import { AthleteResolver } from "./athletes/resolvers/athlete-resolver.service";
import { SessionResolver } from "./sessions/resolvers/session-resolver.service";
import { ExerciseResolverService } from "./exercices/resolvers/exercise-resolver.service";
import { TestResolver } from "./test/resolvers/test-resolver";
import {
  OrganizationsOrganizationMembersPageComponent
} from "./organizations/pages/organization-members/organization-members";
import { OrganizationsSettingsPageComponent } from "./organizations/pages/settings/settings";
import { OrganizationsUsersPageComponent } from "./organizations/pages/users/users";
import { ExercicesListPageComponent } from "./exercices/pages/list/list";
import { ExercicesLibraryRedirect } from "./exercices/guards/library-redirect";
import { AthletesListPageComponent } from "./athletes/pages/list/list";
import { AnnualPlanLibraryComponent } from "./annual-plan/pages/annual-plans-library/annual-plan-library.component";
import {
  AnnualPlanLibraryShowComponent
} from "./annual-plan/pages/annual-plans-library-show/annual-plan-library-show.component";
import { AnnualPlanDataResolver } from "./annual-plan/resolvers/annual-plan-data-resolver.service";
import {
  AnnualPlanAthleteListComponent
} from "./annual-plan/pages/annual-plans-athlete-list/annual-plan-athlete-list.component";
import {
  AnnualPlanAthleteShowComponent
} from "./annual-plan/pages/annual-plans-athlete-show/annual-plan-athlete-show.component";
import {
  EvaluationsAthleteEditComponent
} from "./evaluation-athlete/pages/evaluations-athlete-edit/evaluations-athlete-edit.component";
import { EvaluationAthleteResolver } from "./evaluation-athlete/resolvers/evaluation-athlete-resolver.service";
import { CoachesListPageComponent } from "./coaches/pages/list/list";
import { CoachViewPageComponent } from "./coaches/pages/view/view";
import { CoachResolver } from "./coaches/resolvers/coach-resolver.service";
import { GuestsListPageComponent } from "./guests/pages/list/list";
import { LibraryService } from "./libraries/providers/library.services";
import { LibraryResolver } from "./libraries/resolvers/library-resolver.service";
import { CurrentUserResolver } from "./common/resolvers/current-user.resolver";
import { AthleteViewPageComponent } from "./athletes/pages/view/view";
import { StatsContainerComponent } from "./stats/pages/stats-container/stats-container.component";
import { TeamsListPageComponent } from "./teams/pages/list/list";
import { TeamViewPageComponent } from "./teams/pages/view/view";
import { MobileDashboardComponent } from "./mobile/dashboard/mobile.dashboard.component";
import { CanActivateDailyFeedbackAnswered } from "./feedback/guard/can-activate-daily-feedback-answered";
import { MobileDiaryListComponent } from "./mobile/diary/mobile.diary-list.component";
import { MobileChatComponent } from "./mobile/chat/mobile.chat.component";
import { MobileDiaryShowComponent } from "./mobile/diary/mobile.diary-show.component";
import { TimeslotResolver } from "./agendas/resolvers/timeslot-resolver.service";
import { MobileFeedbackListContainerComponent } from "./mobile/feedback/mobile.feedback-list-container.component";
import { MobileFeedbackShowContainerComponent } from "./mobile/feedback/mobile.feedback-show-container.component";
import { FeedbackAthleteAnswerResolver } from "./feedback/resolvers/feedback-athlete-answer-resolver.service";
import {
  MobileFeedbackDailyNewContainerComponent
} from "./mobile/feedback/mobile.feedback-daily-new-container.component";
import { FeedbackResolver } from "./feedback/resolvers/feedback-resolver.service";
import {
  MobileFeedbackSessionNewContainerComponent
} from "./mobile/feedback/mobile.feedback-session-new-container.component";
import { MobileExerciseShowComponent } from "./mobile/exercise/exercise-show.component";
import { MobileSessionComponent } from "./mobile/session/mobile.session.component";
import { MobilePlanComponent } from "./mobile/plan/mobile.plan.component";
import { MobileAllTestContainerComponent } from "./mobile/test/all-test-container/mobile.all-test-container.component";
import { MobileTestContainerComponent } from "./mobile/test/test-container/mobile.test-container.component";
import { MobileEvaluationEditComponent } from "./mobile/evaluation/mobile.evaluation.edit.component";
import {
  EvaluationsAthleteShowComponent
} from "./evaluation-athlete/pages/evaluations-athlete-show/evaluations-athlete-show.component";
import { MobileEvaluationShowComponent } from "./mobile/evaluation/mobile.evaluations.show.component";
import { AllTestContainerComponent } from "./test/pages/all-test-container/all-test-container.component";
import { TestContainerComponent } from "./test/pages/test-container/test-container.component";
import {
  CommunicationShowContainerComponent
} from "./communications/pages/communication-show-container/communication-show-container.component";
import {
  FeedbackListContainerComponent
} from "./feedback/pages/feedback-list-container/feedback-list-container.component";
import {
  FeedbackShowContainerComponent
} from "./feedback/pages/feedback-show-container/feedback-show-container.component";
import {
  FeedbackSessionNewContainerComponent
} from "./feedback/pages/feedback-session-new-container/feedback-session-new-container.component";
import {
  FeedbackDailyNewContainerComponent
} from "./feedback/pages/feedback-daily-new-container/feedback-daily-new-container.component";
import {
  DocumentListContainerComponent
} from "./document/pages/document-list-container/document-list-container.component";
import { MobileDocumentListComponent } from "./mobile/document/mobile.document.list.component";
import { MobileEvaluationListComponent } from "./mobile/evaluation/mobile.evaluation.list.component";
import { AgendasCalendarPageComponent } from "./agendas/pages/calendar/calendar";
import { LibraryFromOrganizationResolver } from "./libraries/resolvers/library-from-organization.resolver.service";
import { UserResolver } from "./users/resolvers/user-resolver.service";
import { SessionsListPageComponent } from "./sessions/pages/list/list";
import { ExerciseShowComponent } from "./exercices/pages/show/exercise-show.component";
import { SessionsSessionPageComponent } from "./sessions/pages/session/session";
import { SessionEditorPageComponent } from "./sessions/pages/editor/editor";
import { LibrariesFromOrganizationResolver } from "./libraries/resolvers/libraries-from-organization.resolver.service";
import { MobileUsersEditPageComponent } from "./mobile/profile/mobile.user.edit";
import { UsersCanEditUserGuard } from "./users/guards/can-edit-user";
import { UsersEditPageComponent } from "./users/pages/edit/edit";
import { EulaTermsPageComponent } from "./common/pages/eula-terms/eula-terms";
import { PrivacyTermsPageComponent } from "./common/pages/privacy-terms/privacy-terms";

export const routes: Routes = [
  {
    path: '',
    component: HomePageComponent,
    canActivate: [IsAppReadyGuard, HasHomepageRedirectGuard],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: 'eula-terms',
    component: EulaTermsPageComponent,
    canActivate: [IsAppReadyGuard],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: 'privacy-terms',
    component: PrivacyTermsPageComponent,
    canActivate: [IsAppReadyGuard],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: '401',
    component: Error401PageComponent,
    canActivate: [IsAppReadyGuard],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: '404',
    component: Error404PageComponent,
    canActivate: [IsAppReadyGuard],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: 'logout',
    component: LogoutPageComponent,
    canActivate: [IsAppReadyGuard, IsLoggedInGuard],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: ':organizationCode/app/athletes/:athleteId',
    component: MobileDashboardComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    },
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/diary/:date',
    component: MobileDiaryListComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/chat',
    component: MobileChatComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/diary/:sessionId/:timeslotId',
    component: MobileDiaryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      session: SessionResolver,
      timeSlot: TimeslotResolver
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/feedback',
    component: MobileFeedbackListContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/feedback/:feedbackAthleteAnswerId',
    component: MobileFeedbackShowContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    data: {
      title: 'Feedbacks'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      feedbackAthleteAnswer: FeedbackAthleteAnswerResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/feedback/:feedbackType/new',
    component: MobileFeedbackDailyNewContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'FeedbackDailyNew'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      feedback: FeedbackResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/feedback/:feedbackType/timeslot/:timeslotId/new',
    component: MobileFeedbackSessionNewContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'FeedbackSessionNew'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      feedback: FeedbackResolver,
      timeslot: TimeslotResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/sessions/:sessionId',
    component: MobileSessionComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/plan',
    component: MobilePlanComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/tests',
    component: MobileAllTestContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    data: {
      title: 'Tests'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/tests/:testId',
    component: MobileTestContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'Tests'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      test: TestResolver,
    }
  },
  {
    // affiche le détail d'un exercice
    path: ':organizationCode/app/athletes/:athleteId/exercise/:exerciseId',
    component: MobileExerciseShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      exercise: ExerciseResolverService
    }
  },
  {
    path: 'organizations',
    component: OrganizationsOrganizationSelectPageComponent,
    canActivate: [IsAppReadyGuard, IsLoggedInGuard],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: ':organizationCode',
    component: OrganizationsHomePageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: ':organizationCode/members',
    component: OrganizationsOrganizationMembersPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: ':organizationCode/settings',
    component: OrganizationsSettingsPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: ':organizationCode/settings/users',
    component: OrganizationsUsersPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: ':organizationCode/libraries',
    component: ExercicesListPageComponent,
    canActivate: [IsAppReadyGuard, ExercicesLibraryRedirect, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: ':organizationCode/athletes',
    component: AthletesListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: ':organizationCode/coaches',
    component: CoachesListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      isSuperCoaches: false,
      title: 'Organization_coaches',
      crumb: 'Coaches',
      detailUrl: '/coaches/'
    }
  },
  {
    path: ':organizationCode/coaches/:coachId',
    component: CoachViewPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      crumb: 'Coaches',
      backUrl: '/coaches'
    },
    resolve: {
      organization: OrganizationResolver,
      coach: CoachResolver
    }
  },
  {
    path: ':organizationCode/super-coaches',
    component: CoachesListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      isSuperCoaches: true,
      title: 'Organization_super_coaches',
      crumb: 'Super_Coaches',
      detailUrl: '/super-coaches/'
    }
  },
  {
    path: ':organizationCode/super-coaches/:coachId',
    component: CoachViewPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      crumb: 'Super_Coaches',
      backUrl: '/super-coaches'
    },
    resolve: {
      organization: OrganizationResolver,
      coach: CoachResolver
    }
  },
  {
    path: ':organizationCode/guests',
    component: GuestsListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
    }
  },
  {
    path: ':organizationCode/libraries/:libraryId/plans-elitment',
    component: AnnualPlanLibraryComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Elitment_annual_plans_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
    }
  },
  {
    // affiche les plans annuels de l'organisation
    path: ':organizationCode/libraries/:libraryId/plans-super-coach',
    component: AnnualPlanLibraryComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Super_coaches_annual_plans_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
    }
  },
  {
    // affiche la liste de mes plans annuels
    path: ':organizationCode/libraries/:libraryId/plans/personal',
    component: AnnualPlanLibraryComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      _guardsSequence: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
      context: LibraryService.CONTEXT_USER,
      title: 'Personal_annual_plans_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
    }
  },
  {
    // affiche un plan elitment
    path: ':organizationCode/libraries/:libraryId/plans-elitment/:annualPlanId',
    component: AnnualPlanLibraryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Elitment_annual_plans_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    // affiche un plan super-coach
    path: ':organizationCode/libraries/:libraryId/plans-super-coach/:annualPlanId',
    component: AnnualPlanLibraryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Super_coaches_annual_plans_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    // affiche un de mes plans annuels
    path: ':organizationCode/libraries/:libraryId/plans/personal/:annualPlanId',
    component: AnnualPlanLibraryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      context: LibraryService.CONTEXT_USER,
      title: 'Personal_annual_plans_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    // édition d'un plan elitment
    path: ':organizationCode/libraries/:libraryId/plans-elitment/:annualPlanId/:edit',
    component: AnnualPlanLibraryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Elitment_annual_plans_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    // édition d'un plan super-coach
    path: ':organizationCode/libraries/:libraryId/plans-super-coach/:annualPlanId/:edit',
    component: AnnualPlanLibraryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Super_coaches_annual_plans_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    // édition d'un de mes plans annuels
    path: ':organizationCode/libraries/:libraryId/plans/personal/:annualPlanId/:edit',
    component: AnnualPlanLibraryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_USER,
      title: 'Personal_annual_plans_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    // affiche le plan annuel d'une organisation
    path: ':organizationCode/libraries/:libraryId/plans/:annualPlanId',
    component: AnnualPlanLibraryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Annual_plans'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    // affiche un de mes plans annuels
    path: ':organizationCode/plans/:annualPlanId',
    component: AnnualPlanLibraryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      context: LibraryService.CONTEXT_USER,
      title: 'Annual_plans'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    // édition d'un plan annuel d'une organisation
    path: ':organizationCode/libraries/:libraryId/plans/:annualPlanId/:edit',
    component: AnnualPlanLibraryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Annual_plans'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    // édition d'un de mes plans annuels
    path: ':organizationCode/plans/:annualPlanId/:edit',
    component: AnnualPlanLibraryShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_USER,
      title: 'Annual_plans'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/plans',
    component: AnnualPlanAthleteListComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      _guardsSequence: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
      title: 'Annual_plans'
    },
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/plans/:annualPlanId',
    component: AnnualPlanAthleteShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'Annual_plans'
    },
    resolve: {
      currentUser: CurrentUserResolver,
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/evaluations/:evaluationAthleteId/edit',
    component: EvaluationsAthleteEditComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'EvaluationsAthletes'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      evaluationAthlete: EvaluationAthleteResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/evaluations/:evaluationAthleteId/edit',
    component: MobileEvaluationEditComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    data: {
      title: 'EvaluationsAthletes'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      evaluationAthlete: EvaluationAthleteResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/evaluations/:evaluationAthleteId',
    component: EvaluationsAthleteShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'EvaluationsAthletes'
    },
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      evaluationAthlete: EvaluationAthleteResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/evaluations/:evaluationAthleteId',
    component: MobileEvaluationShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    data: {
      title: 'EvaluationsAthletes'
    },
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      evaluationAthlete: EvaluationAthleteResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/tests',
    component: AllTestContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'Tests'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/tests/:testId',
    component: TestContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'Tests'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      test: TestResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/communications',
    component: CommunicationShowContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      _guardsSequence: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
      title: 'Communications'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/feedback',
    component: FeedbackListContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'Feedbacks'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/feedback/:feedbackAthleteAnswerId',
    component: FeedbackShowContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'Feedbacks'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      feedbackAthleteAnswer: FeedbackAthleteAnswerResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/feedback/:feedbackType/timeslot/:timeslotId/new',
    component: FeedbackSessionNewContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'FeedbackSessionNew'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      feedback: FeedbackResolver,
      timeslot: TimeslotResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/feedback/:feedbackType/new',
    component: FeedbackDailyNewContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'FeedbackDailyNew'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      feedback: FeedbackResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/documents',
    component: DocumentListContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'Documents'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/documents',
    component: MobileDocumentListComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'Documents'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/stats',
    component: StatsContainerComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    data: {
      title: 'Stats'
    },
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/evaluations',
    component: EvaluationsAthleteShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    },
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/evaluations',
    component: MobileEvaluationListComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService, CanActivateDailyFeedbackAnswered],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    },
  },
  {
    path: ':organizationCode/athletes/:athleteId/plans/:annualPlanId/:edit',
    component: AnnualPlanAthleteShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      title: 'Annual_plans'
    },
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      annualPlanData: AnnualPlanDataResolver,
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId',
    component: AthleteViewPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/calendar/:agendaId/:date/:editMode',
    component: AgendasCalendarPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      library: LibraryFromOrganizationResolver
    }
  },
  {
    path: ':organizationCode/athletes/:athleteId/calendar/:agendaId/:date',
    component: AgendasCalendarPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
      library: LibraryFromOrganizationResolver
    }
  },
  {
    path: ':organizationCode/libraries/:libraryId/exercises',
    component: ExercicesListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Coaches_exercises_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver
    }
  },
  {
    path: ':organizationCode/libraries/:libraryId/exercises-super-coach',
    component: ExercicesListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Super_coaches_exercises_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver
    }
  },
  {
    path: ':organizationCode/libraries/:libraryId/exercises-elitment',
    component: ExercicesListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Elitment_exercises_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver
    }
  },
  {
    path: ':organizationCode/libraries/:libraryId/exercises/personal',
    component: ExercicesListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_USER,
      title: 'Personal_exercises_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver
    }
  },
  {
    path: ':organizationCode/libraries/:libraryId/sessions-elitment',
    component: SessionsListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Elitment_sessions_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
    }
  },
  {
    // affiche la liste des séances de l'organisation
    path: ':organizationCode/libraries/:libraryId/sessions-super-coach',
    component: SessionsListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Super_coaches_sessions_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
    }
  },
  {
    // affiche la liste de mes séances
    path: ':organizationCode/libraries/:libraryId/sessions/personal',
    component: SessionsListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_USER,
      title: 'Personal_sessions_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver
    }
  },
  {
    // affiche une séance elitment
    path: ':organizationCode/libraries/:libraryId/sessions-elitment/:sessionId',
    component: SessionsSessionPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Elitment_sessions_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      session: SessionResolver
    }
  },
  {
    // affiche une séance super-coach
    path: ':organizationCode/libraries/:libraryId/sessions-super-coach/:sessionId',
    component: SessionsSessionPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Super_coaches_sessions_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      session: SessionResolver
    }
  },
  {
    // affiche une de mes séances
    path: ':organizationCode/libraries/:libraryId/sessions/personal/:sessionId',
    component: SessionsSessionPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_USER,
      title: 'Personal_sessions_title'
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      session: SessionResolver
    }
  },
  {
    // édition d'une séance elitment
    path: ':organizationCode/libraries/:libraryId/sessions-elitment/:sessionId/editor',
    component: SessionEditorPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Elitment_sessions_title'
    },
    resolve: {
      organization: OrganizationResolver,
      libraries: LibrariesFromOrganizationResolver,
      library: LibraryResolver,
      session: SessionResolver,
    }
  },
  {
    // édition d'une séance super-coach
    path: ':organizationCode/libraries/:libraryId/sessions-super-coach/:sessionId/editor',
    component: SessionEditorPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION,
      title: 'Super_coaches_sessions_title'
    },
    resolve: {
      organization: OrganizationResolver,
      libraries: LibrariesFromOrganizationResolver,
      library: LibraryResolver,
      session: SessionResolver,
    }
  },
  {
    // édition d'une de mes séances
    path: ':organizationCode/libraries/:libraryId/sessions/personal/:sessionId/editor',
    component: SessionEditorPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_USER,
      title: 'Personal_sessions_title'
    },
    resolve: {
      organization: OrganizationResolver,
      libraries: LibrariesFromOrganizationResolver,
      library: LibraryResolver,
      session: SessionResolver,
    }
  },
  {
    // affiche le détail d'un exercice
    path: ':organizationCode/exercise/:exerciseId',
    component: ExerciseShowComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [],
    data: {
    },
    resolve: {
      organization: OrganizationResolver,
      exercise: ExerciseResolverService
    }
  },
  // {
  //     !! apparemment en double avec la rouze juste ci-dessous
  //     !! le commentaire indique "exercice" mais la route s'occupe de "sessions"
  //     // affiche le détail d'un exercice
  //     path: ':organizationCode/sessions/:sessionId',
  //     component: SessionViewComponent,
  //     canActivate: [GuardsSequenceGuard],
  //     canDeactivate: [],
  //     data: {
  //         _guardsSequence: [IsAppReadyGuard, IsOrganizationMemberGuard],
  //     },
  //     resolve: {
  //         organization: OrganizationResolver,
  //     }
  // },
  {
    // affiche la séance d'une organisation
    path: ':organizationCode/libraries/:libraryId/sessions/:sessionId',
    component: SessionsSessionPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      session: SessionResolver
    }
  },
  {
    // affiche une de mes séance
    path: ':organizationCode/sessions/:sessionId',
    component: SessionsSessionPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION
    },
    resolve: {
      organization: OrganizationResolver,
      session: SessionResolver,
    }
  },
  {
    // édition d'une séance de l'organisation
    path: ':organizationCode/libraries/:libraryId/sessions/:sessionId/editor',
    component: SessionEditorPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_ORGANIZATION
    },
    resolve: {
      organization: OrganizationResolver,
      libraries: LibrariesFromOrganizationResolver,
      library: LibraryResolver,
      session: SessionResolver,
    }
  },
  {
    // édition d'une de mes séance
    path: ':organizationCode/sessions/:sessionId/editor',
    component: SessionEditorPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    data: {
      context: LibraryService.CONTEXT_USER
    },
    resolve: {
      organization: OrganizationResolver,
      library: LibraryResolver,
      libraries: LibrariesFromOrganizationResolver,
      session: SessionResolver,
    }
  },
  {
    path: ':organizationCode/teams',
    component: TeamsListPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: ':organizationCode/teams/:teamId',
    component: TeamViewPageComponent,
    canActivate: [IsAppReadyGuard, IsOrganizationMemberGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: 'auth?*nextUrl=*',
    component: HomePageComponent,
    canActivate: [IsAppReadyGuard],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: ':organizationCode/app/athletes/:athleteId/users/:userId/edit',
    component: MobileUsersEditPageComponent,
    canActivate: [IsAppReadyGuard, UsersCanEditUserGuard, AthleteViewGuardService],
    canDeactivate: [HasPendingChangesGuard],
    resolve: {
      organization: OrganizationResolver,
      athlete: AthleteResolver,
    }
  },
  {
    path: 'users/:userId/edit',
    component: UsersEditPageComponent,
    canActivate: [IsAppReadyGuard, UsersCanEditUserGuard],
    canDeactivate: [HasPendingChangesGuard],
  },
  {
    path: '**',
    component: Error404PageComponent,
    canActivate: [IsAppReadyGuard],
    canDeactivate: [HasPendingChangesGuard],
  }
];
